import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import { useTranslation } from 'react-i18next';
import CcmStatusSelectBox from 'components/molecules/ccm-status-select-box';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import DoctorSelectBox from 'components/molecules/doctor-select-box';
import PatientSelectBox from 'components/molecules/patient-select-box';
import { useOnboardingStatusQueryFilter } from 'infrastructure/hooks/ccm/onboarding-status-report';
import { type FC } from 'react';

import s from './styles.module.scss';

import type { ICcmOnboardingStatusReportFilters } from 'infrastructure/interfaces';

interface CcmOnboardingFilterProps {}

const CcmOnboardingFilter: FC<CcmOnboardingFilterProps> = () => {
  const { count, filter, resetFilter, updateFilter } =
    useOnboardingStatusQueryFilter();
  const { t } = useTranslation();

  const onClinicChange = (clinicGuids: string[]) => {
    const newFilter: Partial<ICcmOnboardingStatusReportFilters> = {
      clinicGuid: clinicGuids,
      doctorGuid: undefined,
      patientGuid: undefined,
      page: undefined,
    };

    if (!clinicGuids.length) {
      newFilter.childClinic = undefined;
    }
    updateFilter(newFilter);
  };

  const onChildClinicChange = (value: any) => {
    const childClinic = typeof value === 'boolean' ? String(value) : value;
    updateFilter({ childClinic, page: undefined });
  };

  const onDoctorChange = (doctorGuid?: string) => {
    updateFilter({
      doctorGuid,
      patientGuid: undefined,
      page: undefined,
    });
  };

  const onReset = () => {
    resetFilter();
  };

  return (
    <ExpandableFilterPanel filtersCount={count} onReset={onReset} isExpanded>
      <div className={s.wrapper}>
        <ExpandableFilterPanelItem
          borderBottom
          label={t('labels.patientFilter')}
        >
          <PatientSelectBox
            clinicGuid={filter.clinicGuid}
            doctorGuid={filter.doctorGuid}
            placeholder={t('labels.searchPatient')}
            value={filter.patientGuid}
            onChange={(patientGuid) => {
              updateFilter({ patientGuid, page: undefined });
            }}
            allowClear
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.ccmStatus')} borderBottom>
          <CcmStatusSelectBox
            value={filter.ccmStatuses}
            onChange={(v) => {
              updateFilter({ ccmStatuses: v, page: undefined });
            }}
            allowClear
            mode="multiple"
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.pcmStatus')} borderBottom>
          <BooleanSelectBox
            withAll
            allowClear
            value={filter.pcmStatus}
            onChange={(v) => {
              const pcmStatus = typeof v === 'boolean' ? String(v) : v;
              updateFilter({ pcmStatus, page: undefined });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.clinic')}>
          <ClinicSelectBox
            allowClear
            placeholder={t('labels.search')}
            value={filter.clinicGuid}
            onChange={(clinicGuid, options) => {
              if (Array.isArray(clinicGuid) && Array.isArray(options)) {
                onClinicChange(clinicGuid);
              } else {
                onClinicChange([clinicGuid]);
              }
            }}
            mode="multiple"
            parentClinicsOnly={false}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.childClinics')}>
          <BooleanSelectBox
            value={filter.childClinic}
            trueLabel={t('labels.included')}
            falseLabel={t('labels.excluded')}
            onChange={onChildClinicChange}
            disabled={!filter.clinicGuid?.length}
            allowClear
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.provider')}>
          <DoctorSelectBox
            allowClear
            clinicGuid={filter.clinicGuid}
            placeholder={t('labels.search')}
            roles={[]}
            value={filter.doctorGuid}
            onChange={onDoctorChange}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};
export default CcmOnboardingFilter;
